<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid, reset, errors}" ref="observer">
    <artisio-modal size="lg" :visible="modal.show" :data="model" scrollable
                   :title="model.uuid ? $t(`Update Receipt`) : $t(`Create New Receipt`)" @show="onShow"
                   @hidden="onHidden" @ok.prevent="onSubmit" :show-confirmation="showConfirmation"
                   :ok-title="$t('Submit')" :is-loading="loading" modal-class="mobile-full-screen">
      <div class="row">
        <div class="col-md-4">
          <input-widget :readonly="isReceiptNoReadonly || model.uuid" :model="model" attribute="receipt_prefix"/>
        </div>
        <div class="col-md-4">
          <input-widget :model="model" attribute="receipt_type" value-field="key"
                        display-field="${item.label}" @select2Select="onReceiptTypeChange"
                        type="select2" :items="receiptTypes" :disabled="isReceiptNoReadonly || model.uuid"/>
        </div>
        <div class="col-md-4">
          <input-widget :model="model" attribute="date" type="datepicker"/>
        </div>
        <div class="col-md-10">
          <customer-select-input-widget :model="model" attribute="customer_uuid"/>
        </div>
        <div class="col-md-2 text-right">
          <b-tooltip target="new-customer-btn" :title="$t('New Customer')"/>
          <b-button variant="primary" style="margin-top: 2em" id="new-customer-btn" @click="onNewCustomerClick">
            <font-awesome-icon icon="user-plus"/>
          </b-button>
        </div>
        <div class="col-md-6">
          <department-select-input-widget :model="model" attribute="department_uuid" @select="onDepartmentSelect"
                                          @unselect="onDepartmentUnselect"/>
        </div>
        <div class="col-md-6">
          <customer-select-input-widget :model="model" attribute="recommended_by_uuid"/>
        </div>
        <div class="col-md-6">
          <user-select-input-widget :model="model" attribute="recommended_by_user_uuid"/>
        </div>
        <div class="col-md-6">
          <user-select-input-widget :model="model" attribute="rep_user_uuid"/>
        </div>
        <div class="col-md-6">
          <customer-select-input-widget :model="model" attribute="dropper_uuid"/>
        </div>
        <div class="col-md-12">
          <user-select-input-widget :model="model" attribute="specialists_uuids" multiple :items="model.specialists"
                                    :disabled="!model.department_uuid" :filter-extra-data="specialistExtraFilters"/>
        </div>
        <div class="col-md-6">
          <currency-input-widget :model="model" attribute="insurance_value"/>
        </div>
        <div class="col-md-6">
          <input-widget :model="model" attribute="containers_count" type="number"/>
        </div>
        <div class="col-md-6">
          <input-widget :model="model" attribute="container_type"/>
        </div>
        <fieldset class="mb-2 pb-1">
          <legend style="font-size: 1rem;">{{$t('Item Location')}}</legend>
          <div class="col-md-12">
            <template v-for="(itemLocation, index) in model.item_locations">
              <div class="row">
                <div class="col-md-3">
                  <item-location-select-input-widget :model="itemLocation"
                                                     attribute="item_location_uuid"/>
                </div>
                <div class="col-md-3">
                  <input-widget v-if="receiptContainerOptions && receiptContainerOptions.length"
                                type="select2" :display-field="(item) => item.value" :value-field="'value'"
                                :model="itemLocation" attribute="container_type" :items="receiptContainerOptions" />
                  <input-widget v-else :model="itemLocation" attribute="container_type" :multiple="false"/>
                </div>
                <div class="col-md-2">
                  <input-widget :model="itemLocation" attribute="container_count" type="number"/>
                </div>
                <div class="col-md-3">
                  <div class="d-flex flex-column">
                    <label class="mb-1">
                      {{ $t('Make Primary') }}
                    </label>
                    <div class="d-flex">
                      <input-widget :model="itemLocation"
                                    attribute="is_primary" type="checkbox" :label="false" is-switch
                                    @change="(val) => itemLocationPrimaryChange(index,val)"
                                    class="mt-2"/>
                      <field-title-hint
                        :title="$t('A consignment can have only one primary location, which all added inventory items will inherit by default, unless changed manually.')"
                        style="font-size: 1.5rem;"
                        :target="`item-location-is-primary-${index}`"/>
                    </div>
                  </div>
                </div>
                <div class="col-md-1">
                  <b-button v-if="model.item_locations.length > 1" variant="danger" class="float-right"
                            size="md"
                            style="margin-right: 1em"
                            @click="removeItemLocation(index)">
                    <font-awesome-icon :icon="'trash'"></font-awesome-icon>
                  </b-button>
                </div>
              </div>
              <div class="row">
                <div class="col-md-8">
                  <input-widget :model="itemLocation" attribute="note" type="textarea"/>
                </div>
              </div>
              <hr v-if="index !== model.item_locations.length - 1"/>
            </template>
          </div>
          <div class="col-md-10">
            <a href="#" @click="addEmptyItemLocation">{{ $t('+ New Location') }}</a>
          </div>
        </fieldset>
        <div class="col-md-6" v-if="enableReceiptStatus">
          <input-widget :model="model" attribute="status" value-field="value" display-field="${item.label}"
                        type="select2" :items="receiptStatuses"/>
        </div>
        <div class="col-md-12">
          <input-widget :model="model" attribute="description" type="textarea"/>
        </div>
      </div>
      <translatable-input-widget :hint="false" :model="model" :fields="model.sorted_dynamic_fields_template"
                                 :errors="errors"/>
      <miscellaneous-item-tiers-form :model="model" v-if="false"/>
    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import InputWidget from "../../core/components/input-widget/InputWidget";
  import ReceiptModel from "./Receipt.model";
  import {createNamespacedHelpers} from "vuex";
  import ArtisioModal from "../../core/components/artisio-modal/ArtisioModal";
  import CustomerSelectInputWidget from "../../core/widgets/CustomerSelectInputWidget";
  import UserSelectInputWidget from "../../core/widgets/UserSelectInputWidget";
  import DepartmentSelectInputWidget from "../../core/widgets/DepartmentSelectInputWidget";
  import TranslatableInputWidget from "../../core/widgets/TranslatableInputWidget";
  import {
    SOURCE_LOT_UP_MODAL,
    SOURCE_RECEIPT_DASHBOARD,
    SOURCE_RECEIPT_MODAL,
    SOURCE_RECEIPT_VIEW
  } from "../constants";
  import MiscellaneousItem from "./MiscellaneousItem.model";
  import CustomerService from "../customer/CustomerService";
  import MiscellaneousItemTiersForm from "@/modules/receipt/MiscellaneousItemTiersForm";
  import CurrencyInputWidget from "@/core/widgets/CurrencyInputWidget";
  import ReceiptService from "@/modules/receipt/ReceiptService";
  import ItemLocationSelectInputWidget from "@/core/widgets/ItemLocationSelectInputWidget.vue";
  import ReceiptItemLocationModel from "@/modules/receipt/ReceiptItemLocation.model";
  import FieldTitleHint from "@/modules/inventory/components/FieldTitleHint.vue";

  const {mapState, mapActions} = createNamespacedHelpers('receipts');
  const {mapActions: lotsMapsActions} = createNamespacedHelpers('lots');
  const {mapActions: customerMapActions} = createNamespacedHelpers('customer');
  const {mapState: globalState} = createNamespacedHelpers('global');

  export default {
    name: "ReceiptModal",
    components: {
      FieldTitleHint,
      ItemLocationSelectInputWidget,
      CurrencyInputWidget,
      MiscellaneousItemTiersForm,
      DepartmentSelectInputWidget,
      TranslatableInputWidget,
      UserSelectInputWidget,
      CustomerSelectInputWidget,
      InputWidget,
      ArtisioModal
    },
    data() {
      return {
        loading: false,
        isReceiptNoReadonly: false,
        showConfirmation: true,
        model: new ReceiptModel(),
        errorMessage: null,
      }
    },
    computed: {
      ...mapState({
        modal: state => state.modal,
        editObject: state => state.modal.object
      }),
      ...globalState({
        currentBranch: state => state.currentBranch,
        defaultMiscellaneousItemCharges: state => state.defaultMiscellaneousItemCharges,
        enableReceiptStatus: state => state.enableReceiptStatus,
        receiptStatuses: state => state.receiptStatuses,
        receiptTypes: state => state.receiptTypes,
        inclusiveTaxForFinancialObjects: state => state.inclusiveTaxForFinancialObjects,
        receiptContainerOptions: state => state.receiptContainerOptions,
      }),
      specialistExtraFilters() {
        return {specialist_department_uuid: this.model && this.model.department_uuid ? this.model.department_uuid : null};
      }
    },
    watch: {
      editObject() {
        this.isReceiptNoReadonly = false;

        if (this.editObject && (this.editObject.uuid || this.editObject.customer_uuid || this.editObject.receipt_no)) {

          if (this.editObject.uuid || (!this.editObject.uuid && this.editObject.receipt_no)) {
            this.isReceiptNoReadonly = true;
          }

          this.model = new ReceiptModel(this.editObject);
          this.initializeDynamicFields(this.model.receipt_type);
          this.model.miscellaneous_items = this.model.miscellaneous_items.map(item => {
            if (item.taxable && this.inclusiveTaxForFinancialObjects) {
              let cfc = this.currentBranch.tax_rate / 100 + 1
              item.amount = Math.round(parseFloat(item.amount * cfc))
            }
            return new MiscellaneousItem(item)
          });
        }
      },
      'model.customer_uuid': {
        deep: true,
        async handler() {
          this.validateCustomerAndRecommendedBy();
          if (this.model && this.model.customer_uuid) {
            this.errorMessage = null;
            let customerResponse = await CustomerService.getCustomer(this.model.customer_uuid, 'term');
            if (customerResponse.success && customerResponse.body.term.identity_verification !== 'verified') {
              this.errorMessage = this.$i18n.t('Identification check not approved for following customer.');
            }
          }
        }
      },
      'model.recommended_by_uuid': {
        deep: true,
        async handler() {
          this.validateCustomerAndRecommendedBy();
        }
      },
    },
    methods: {
      ...lotsMapsActions(['updateModalReceipt']),
      ...mapActions(['hideModal', 'updateReceipt', 'updateViewReceipt']),
      ...customerMapActions({
        showCustomerModal: 'showModal'
      }),
      onShow() {
        this.showConfirmation = true
        this.initializeDynamicFields(null, true);
        if (!this.model.item_locations || !this.model.item_locations.length) {
          this.model.item_locations = [];
          this.addEmptyItemLocation();
        }
        if (!this.model.uuid) {
          let defaultReceiptType = this.receiptTypes.find(rt => rt.is_default)
          this.model.receipt_type = defaultReceiptType ? defaultReceiptType.key : null

          if (!defaultReceiptType || defaultReceiptType.key === 'default') {
            this.model.receipt_prefix = this.getBranchDefaultPrefix()
          } else {
            this.model.receipt_prefix = defaultReceiptType.prefix
          }

          if (this.defaultMiscellaneousItemCharges) {
            for (let i in this.defaultMiscellaneousItemCharges) {
              this.model.miscellaneous_items.push(new MiscellaneousItem(this.defaultMiscellaneousItemCharges[i], true))
            }
          }
        }
      },
      getBranchDefaultPrefix() {
        return this.currentBranch && this.currentBranch.receipt_prefix ? this.currentBranch.receipt_prefix : '';
      },
      onHidden() {
        this.model = new ReceiptModel();
        this.hideModal();
      },
      async onSubmit() {
        await this.$refs.observer.reset();
        let validateSuccess = await this.$refs.observer.validate();
        if (!validateSuccess) {
          return;
        }
        if (this.model.hasError('recommended_by_uuid')) {
          return;
        }
        let response;
        this.loading = true;
        this.model.resetErrors()

        this.model.assignToDynamicFields();
        this.model.enable_multiple_locations = this.model.item_locations.length > 1;
        this.model.item_locations.forEach(il => {
          if (il.note) {
            il.note = il.note.trim();
          }
        })
        let requestData = {
          ...this.model,
          miscellaneous_items: this.model.miscellaneous_items.filter(item => (item.description || item.description_dropdown_value) && item.amount).map(item => {
            if (item.taxable && this.inclusiveTaxForFinancialObjects) {
              let cfc = this.currentBranch.tax_rate / 100 + 1
              item.amount = parseFloat(item.amount / cfc).toFixed(2)
            }
            return item
          }),
        };

        // Treeselect returns undefined instead of null and it gets skipped from requests. Thats why I have added
        // this workaround in here.
        if (requestData.item_location_uuid === undefined) {
          requestData.item_location_uuid = null;
        }

        if (!this.model.uuid) {
          response = await ReceiptService.createReceipt(requestData);
        } else {
          response = await this.updateReceipt(requestData);
        }
        this.loading = false;

        if (response.success) {
          this.showConfirmation = false;
          this.hideModal();
          if (!this.model.uuid) {
            this.$toast('success', this.$t('Receipt was successfully created'));
            if (this.modal.source === SOURCE_LOT_UP_MODAL) {
              this.updateModalReceipt(response.body);
            } else {
              this.$router.push({
                name: 'receipt.view',
                params: {id: response.body.uuid},
              });
            }
          } else {
            this.$toast('success', this.$t('Receipt was successfully updated'));
            if (this.modal.source === SOURCE_RECEIPT_VIEW || this.modal.source === SOURCE_RECEIPT_DASHBOARD) {
              this.updateViewReceipt(response.body);
            }
          }
        } else {
          if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0]);
          } else if (response.toastError) {
            this.$errorDialog(response.toastError);
          } else {
            this.model.setMultipleErrors(response.body);
            for (let index in response.body.miscellaneous_items) {
              this.model.miscellaneous_items[index].setMultipleErrors(response.body.miscellaneous_items[index]);
            }
          }
        }
      },
      onNewCustomerClick() {
        this.showCustomerModal({
          source: SOURCE_RECEIPT_MODAL
        })
      },
      validateCustomerAndRecommendedBy() {
        if (this.model.customer_uuid && this.model.recommended_by_uuid
          && this.model.customer_uuid === this.model.recommended_by_uuid) {
          this.model.addError('recommended_by_uuid', this.$t('Recommended by customer cannot be same as the Vendor'));
        } else {
          this.model.resetErrors('recommended_by_uuid');
        }
      },
      onDepartmentSelect() {
        this.model.specialists_uuids = [];
        this.model.specialists = [];
      },
      onDepartmentUnselect() {
        this.model.specialists_uuids = [];
        this.model.specialists = [];
      },
      initializeDynamicFields(key = null, fallbackToDefault = false) {
        if (this.currentBranch.receipt_dynamic_fields_template) {

          if (this.currentBranch.receipt_dynamic_fields_template[key]) {
            this.model.initDynamicFields(this.model, this.currentBranch.receipt_dynamic_fields_template[key])
          } else {
            if (fallbackToDefault) {
              this.model.initDynamicFields(this.model, this.currentBranch.receipt_dynamic_fields_template['default'])
            } else {
              this.model.initDynamicFields(this.model, {})
            }
          }

        }
      },
      onReceiptTypeChange(receiptType) {
        if (receiptType.prefix) {
          this.model.receipt_prefix = receiptType.prefix
        } else {
          if (receiptType.key === 'default') {
            this.model.receipt_prefix = this.getBranchDefaultPrefix()
          } else {
            this.model.receipt_prefix = ''
          }
        }

        this.initializeDynamicFields(receiptType.key)
      },
      addEmptyItemLocation() {
        this.model.item_locations.push(new ReceiptItemLocationModel({is_primary: !this.model.item_locations.length}));
      },
      async removeItemLocation(index) {
        if (this.model.item_locations.length <= 1 || !(await this.$confirm(this.$t('Are you sure you want to remove item location?')))) {
          return;
        }
        this.model.item_locations.splice(index,1);
        if (!this.model.item_locations.some(itemLocation => itemLocation.is_primary)) {
          this.model.item_locations[0].is_primary = true;
        }
      },
      itemLocationPrimaryChange(index, val) {
        this.$nextTick(() => {
          if (val) {
            for (let i in this.model.item_locations) {
              if (index !== parseInt(i)) {
                this.model.item_locations[i].is_primary = false;
              }
            }
          } else {
            if (this.model.item_locations.length === 1) {
              this.model.item_locations[0].is_primary = true;
              this.$toast('danger', this.$t('You should have at least one primary location.'));
            } else {
              this.model.item_locations[index === 0 ? 1 : 0].is_primary = true;
            }
          }
          this.model = new ReceiptModel({...this.model});
        });

      }
    }
  }
</script>

<style scoped>

</style>
